import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { usePostHog } from "@posthog/react";
import Modal from "../ui/Modal/Modal";
import Button from "../ui/Button/Button";
import { COMPANY_EMAIL, COMPANY_EMAIL_MAILTO } from "../../constants/company";
import { useAuth } from "../../hooks/useAuth";
import { captureFeedbackSubmitted } from "../../lib/analytics";
import { feedbackApi } from "../../lib/feedback";
import { apiErrorToMessage } from "../../utils/ApiUtils";
import type { FeedbackTypeDto } from "../../types/types";

type FeedbackModalProps = {
  isOpen: boolean;
  onClose: () => void;
};

const feedbackTypeOptions: Array<{ value: FeedbackTypeDto; label: string }> = [
  { value: "QUESTION", label: "Question" },
  { value: "BUG", label: "Bug report" },
  { value: "FEATURE_IDEA", label: "Feature idea" },
  { value: "GENERAL_FEEDBACK", label: "General feedback" },
];

const FeedbackModal = ({ isOpen, onClose }: FeedbackModalProps) => {
  const posthog = usePostHog();
  const { user } = useAuth();
  const location = useLocation();
  const [type, setType] = useState<FeedbackTypeDto>("QUESTION");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    setType("QUESTION");
    setEmail(user?.email ?? "");
    setMessage("");
    setError(null);
    setSuccess(false);
    setIsSubmitting(false);
  }, [isOpen, user?.email]);

  const sourcePath = `${location.pathname}${location.search}${location.hash}`;

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const trimmedEmail = email.trim();
    const trimmedMessage = message.trim();
    if (!trimmedEmail || !trimmedMessage) {
      setError("Please add your email and a short message.");
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      await feedbackApi.submit({
        type,
        email: trimmedEmail,
        message: trimmedMessage,
        sourcePath,
      });
      captureFeedbackSubmitted(posthog, { feedback_type: type, source_path: sourcePath });
      setSuccess(true);
    } catch (submissionError) {
      setError(apiErrorToMessage(submissionError, "We could not send your message right now."));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Feedback & Questions"
      panelClassName="max-w-xl"
      bodyClassName="pr-0"
    >
      {success ? (
        <div className="space-y-4">
          <p className="text-sm leading-relaxed text-myteengig-text-muted">
            Thanks for reaching out. We read every note while MyTeenGig is still growing, and we&apos;ll follow up if a reply is needed.
          </p>
          <div className="flex justify-end">
            <Button onClick={onClose}>Close</Button>
          </div>
        </div>
      ) : (
        <form onSubmit={(event) => void handleSubmit(event)} className="space-y-4">
          <p className="text-sm leading-relaxed text-myteengig-text-muted">
            Questions, feedback, or something not working? We&apos;d love to hear from you.
          </p>

          <div>
            <label htmlFor="feedback-type" className="mb-2 block text-sm font-medium text-myteengig-text-primary">
              What is this about?
            </label>
            <select
              id="feedback-type"
              value={type}
              onChange={(event) => setType(event.target.value as FeedbackTypeDto)}
              className="w-full rounded-xl border border-myteengig-border bg-white px-4 py-3 text-sm text-myteengig-text-primary outline-none focus:border-myteengig-primary"
            >
              {feedbackTypeOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor="feedback-email" className="mb-2 block text-sm font-medium text-myteengig-text-primary">
              Your email
            </label>
            <input
              id="feedback-email"
              type="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              placeholder="team@example.com"
              className="w-full rounded-xl border border-myteengig-border bg-white px-4 py-3 text-sm text-myteengig-text-primary outline-none focus:border-myteengig-primary"
              autoComplete="email"
            />
          </div>

          <div>
            <label htmlFor="feedback-message" className="mb-2 block text-sm font-medium text-myteengig-text-primary">
              Message
            </label>
            <textarea
              id="feedback-message"
              value={message}
              onChange={(event) => setMessage(event.target.value)}
              rows={6}
              placeholder="Tell us what you noticed, what you were trying to do, or what would make the platform better."
              className="w-full rounded-xl border border-myteengig-border bg-white px-4 py-3 text-sm text-myteengig-text-primary outline-none focus:border-myteengig-primary"
            />
            <p className="mt-2 text-xs text-myteengig-text-muted">
              Prefer direct email? You can always reach us at <a href={COMPANY_EMAIL_MAILTO} className="font-medium text-myteengig-primary underline underline-offset-2">{COMPANY_EMAIL}</a>.
            </p>
          </div>

          {error ? (
            <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              {error}
            </div>
          ) : null}

          <div className="flex items-center justify-end gap-3">
            <Button variant="tertiary" onClick={onClose} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? "Sending..." : "Send feedback"}
            </Button>
          </div>
        </form>
      )}
    </Modal>
  );
};

export default FeedbackModal;
